/**
 * Client-side view of the access claims written by the server
 * (wingman-server/functions/src/services/access.js). The server decides;
 * this only reads the result and applies the expiry timestamp at render time.
 */
export type AccessSource = "stripe" | "sticky" | "admin" | "pass";

export interface AccessState {
  /** Valid right now (expiry already applied). */
  hasAccess: boolean;
  accessSource: AccessSource | null;
  /** Epoch millis, or null for open-ended access. */
  accessExpiresAt: number | null;
  /** Raw Stripe subscription status, if the user has one (any status). */
  stripeStatus: string | null;
  /** Ever held any entitlement. false = never paid; true + !hasAccess = expired. */
  everEntitled: boolean;
}

export const NO_ACCESS: AccessState = {
  hasAccess: false,
  accessSource: null,
  accessExpiresAt: null,
  stripeStatus: null,
  everEntitled: false,
};

const LIVE_STRIPE = ["active", "trialing"];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const deriveAccessFromClaims = (claims: any, now = Date.now()): AccessState => {
  if (!claims) return NO_ACCESS;

  if (typeof claims.hasAccess === "boolean") {
    const accessExpiresAt =
      typeof claims.accessExpiresAt === "number" ? claims.accessExpiresAt : null;
    const stillValid = !accessExpiresAt || accessExpiresAt > now;
    return {
      hasAccess: claims.hasAccess && stillValid,
      accessSource: claims.hasAccess ? (claims.accessSource ?? null) : null,
      accessExpiresAt,
      stripeStatus: claims.stripeStatus ?? null,
      everEntitled: Boolean(claims.everEntitled),
    };
  }

  // Legacy claims (token issued before the user was recomputed).
  const stripeStatus: string | null = claims.subscriptionStatus ?? null;
  const passExpiresAt: number | null =
    typeof claims.sevenDayAccessExpiresAt === "number" && claims.sevenDayAccessExpiresAt > 0
      ? claims.sevenDayAccessExpiresAt
      : null;
  const stripeLive = stripeStatus !== null && LIVE_STRIPE.includes(stripeStatus);
  const passValid = claims.hasSevenDayAccess === true && passExpiresAt !== null && passExpiresAt > now;

  return {
    hasAccess: stripeLive || passValid,
    accessSource: stripeLive ? "stripe" : passValid ? "pass" : null,
    accessExpiresAt: stripeLive ? null : passValid ? passExpiresAt : null,
    stripeStatus,
    everEntitled: Boolean(stripeStatus) || passExpiresAt !== null,
  };
};

/** Stripe is retrying a failed payment; show "update card" rather than the paywall. */
export const needsPaymentUpdate = (access: AccessState) =>
  !access.hasAccess &&
  (access.stripeStatus === "past_due" || access.stripeStatus === "unpaid");
